/* ═══════════════════════════════════════════════════════
   SwipeHome Production Web App — Entry Point
   ES module entry that imports all domain modules and wires
   them into the router + window globals the inline onclick
   handlers rely on. All feature modules are strict TypeScript
   as of Phase 3g.
   ═══════════════════════════════════════════════════════ */

// ─── Core modules ─────────────────────────────────────
import { API, api, setAuthFailHandler } from './api.js';
import { navigate, goBack, render, renderTabBar, registerScreens, setTabBarRenderer } from './router.js';
import {
  clearFeedState,
  getUserLocation,
  restoreFeedState,
  restoreViewPrefs,
  saveFeedState,
  state,
} from './state.js';
import { formatPrice } from '../../shared/format.js';
import {
  timeAgo, getInitials, formatListingAddress, ListControls,
  getMediaUrl, normalizePhotoUrl, imgFallback, getAllMedia,
  showToast, escapeHtml, togglePasswordVisibility,
  getUserName,
  openModal, closeModal,
  setUiNavigate, setUiRender, setUiRenderTabBar,
} from './ui.js';

// ─── Auth screens ────────────────────────────────────
import {
  renderGateKey, handleGateKey,
  renderLogin, handleLogin,
  renderSignup, handleSignupStep1,
  renderRoleSelect, selectRole, handleSignup,
} from './auth.js';

// ─── Investor onboarding ─────────────────────────────
import {
  renderInvestorOnboarding,
  investorOnboardingNext, investorOnboardingBack, investorOnboardingFinish,
  investorOnboardingOnPrice, investorOnboardingOnCap, investorOnboardingOnCoc,
} from './investorOnboarding.js';

// ─── Investor detail sections (Phase 3C) ─────────────
import {
  onInvDownPayment, onInvInterestRate, onInvVacancy, onInvRehab,
  recalcInvestorDeal, saveInvestorDeal, applyInvRentOverride,
  switchOfferStrategy,
} from './investorDetail.js';

// ─── Portfolio + Providers screens (Phase 3D) ────────
import {
  renderPortfolio, openAddPortfolioModal,
  openPortfolioIncomeModal, openPortfolioExpenseModal,
  deletePortfolioProperty, deleteSavedDeal, convertDealToProperty, lookupPropertyAutofill,
  // Phase 1C/1D — detail screen + deal analyzer
  renderPortfolioDetail, openPortfolioDetail, switchPortfolioTab,
  savePortfolioInsurance, clearPortfolioInsurance, dismissInsuranceNudge,
  deletePortfolioTransaction, sortPortfolioBy, openPortfolioQuickTx,
  openDealAnalyzer, onDealAnalyzerInput,
  // Phase 2B — 1031 exchanges
  openExchangeModal, addIdentifiedProperty, removeIdentifiedProperty,
  computeExchangeComparison, toggleExchangeReturnExtension,
  // Phase 2A/2C/2E — depreciation, REPS hours, capex
  openCapexModal, onCapexComponentChange, deleteCapex,
  openRepsModal,
  // Phase 3A/3B/3C — analytics + stress + benchmark
  renderPortfolioAnalytics, switchCompareMetric, onStressSlider,
  // Phase 4 — flip module
  openRehabModal, deleteRehabItem,
  // Phase 5 — tax
  runCapitalGains,
  // Chunk P1 — diversification, FIRE, predictive maintenance, cost-seg
  onFireInput,
  // Chunk P2 — partners + climate
  openPartnerModal, deleteSplit,
  // Mockup polish
  openPortfolioAiStub,
  // Schedule E printable report
  openScheduleEPrint,
} from './portfolio.js';
import {
  renderProviders, applyProvidersFilter, requestProviderIntroFromList,
} from './providers.js';

// ─── Feed & Preferences ─────────────────────────────
import {
  renderPreferences, toggleChip, savePreferences,
  renderFeed, initFeedWithLocation, switchFeedMode, loadFeedForMode,
  loadFeed, refreshFeed,
  runSearch, clearSearch, clearFiltersOnly, openFilterDrawer,
  setFilterChip, toggleFilterType, resetFilters, applyFilters,
  renderCurrentCard, handleSwipe, showCurrentDetail,
  showWeakerDeals,
  toggleMarketChip,
  updateTabIndicator, updateLocationBar,
  buildFeedParams, switchFeedView,
  onInvFilterSlider,
} from './feed.js';

// ─── Saved Searches (Feature 9) ─────────────────────
import {
  saveCurrentSearch, renderSavedSearches,
  applySavedSearchRaw, toggleSavedSearchNotify, confirmDeleteSavedSearch,
} from './savedSearches.js';

// ─── Map View (Feature 4a) ──────────────────────────
// Import from mapLazy so Vite code-splits the Leaflet chunk out
// of the main bundle — it only downloads on first Map tap.
import {
  openMapDetail, closeMapPreview, mapSwipe,
  toggleDrawMode, clearDrawBoundary,
  unlikeListing, likesDrawerSearch, likesDrawerSort, locateMe,
  openPipelineActions, setDealPipelineStatus,
} from './mapLazy.js';

// ─── Listings & Detail ───────────────────────────────
import {
  renderLikes, openDetail, shareListing,
  clearRecentlyViewed,
  toggleCompareMode, toggleCompareSelection, openCompareScreen, renderCompare,
  renderDetail, galleryGo, galleryNav,
} from './listings.js';

// ─── Profile & Onboarding ────────────────────────────
import {
  renderProfile, editProfile, saveProfile, handleLogout, submitPrivacyRequest,
  resetDemoProfile, runDemoReset,
  navigateToHome, loadNotificationCount, loadPreferences,
  renderOnboarding, onboardingNext, onboardingBack,
  completeOnboarding, retryPendingPreferences,
  restoreOnboardingFlags, saveOnboardingFlag,
  renderOnboardingProgressCard, dismissOnboardingProgress,
  handleOnboardingStepClick, showSwipeTutorial, checkSwipeMilestones,
  onboardingUseGPS, onboardingSelectArea, onboardingSaveLocation,
  onboardingUpdateBudget, onboardingSetBudget,
  onboardingSaveBudget,
  onboardingSelectBeds, onboardingSelectBaths, onboardingToggleType,
  onboardingSavePrefs,
  dismissSwipeTutorial,
  loadProfileStats,
} from './profile.js';



// ─── Notifications ───────────────────────────────────
import {
  renderNotifications, loadNotifications, markNotifRead, markAllRead, dismissNotification,
  clearNotificationsPrompt, clearNotifications, setNotifFilter,
  renderNotificationPrefs, loadNotificationPrefs,
  toggleNotifPref, updateQuietHours, toggleBatchDigest,
  NOTIF_PRIORITY_MAP, getNotifPriority, getNotifIcon, getNotifIconType,
  batchNotifications,
} from './notifications.js';

// ─── Trust Layer ─────────────────────────────────────
import {
  isHomecheckDemo, hcScoreColor, hcCondBadge,
  renderHomecheckFull, renderHomecheckComingSoon, toggleHomecheckFactors,
  loadHomeCheck, loadHomecheckSwipeBadge, loadHomecheckListBadges,
} from './trust-layer.js';

// ─── Photo Gallery (Feature 2) ───────────────────────
import {
  openPhotoGallery,
  openPhotoGalleryForListing,
  closePhotoGallery,
  pgNext,
  pgPrev,
} from './photoGallery.js';


// ═══════════════════════════════════════════════════════
// Wire up dependency injection
// ═══════════════════════════════════════════════════════
setUiNavigate(navigate);
setUiRender(render);
setUiRenderTabBar(renderTabBar);
setAuthFailHandler(() => {
  document.body.style.overflow = '';
  state.token = null;
  state.user = null;
  localStorage.removeItem('swipehome_token');
  localStorage.removeItem('swipehome_user');
  state.currentScreen = 'login';
  render();
});

// ═══════════════════════════════════════════════════════
// Register screens with router
// ═══════════════════════════════════════════════════════
registerScreens({
  gateKey: renderGateKey,
  login: renderLogin,
  signup: renderSignup,
  roleSelect: renderRoleSelect,
  preferences: renderPreferences,
  onboarding: renderOnboarding,
  investorOnboarding: renderInvestorOnboarding,
  portfolio: renderPortfolio,
  portfolioDetail: renderPortfolioDetail,
  portfolioAnalytics: renderPortfolioAnalytics,
  providers: renderProviders,
  feed: renderFeed,
  likes: renderLikes,
  detail: renderDetail,
  compare: renderCompare,
  savedSearches: renderSavedSearches,
  profile: renderProfile,
  notifications: renderNotifications,
  notificationPrefs: renderNotificationPrefs,
});

// Set tab bar renderer
setTabBarRenderer(renderTabBar);

// ═══════════════════════════════════════════════════════
// Expose globals for inline onclick handlers
// ═══════════════════════════════════════════════════════
Object.assign(window, {
  // Core
  navigate, goBack, render, showToast, escapeHtml, closeModal, openModal,
  togglePasswordVisibility,
  // State
  state,
  // API
  api, API,
  // Auth
  handleGateKey, handleLogin, handleSignupStep1, handleSignup, selectRole,
  // Investor onboarding
  investorOnboardingNext, investorOnboardingBack, investorOnboardingFinish,
  investorOnboardingOnPrice, investorOnboardingOnCap, investorOnboardingOnCoc,
  // Investor detail (Phase 3C)
  onInvDownPayment, onInvInterestRate, onInvVacancy, onInvRehab,
  recalcInvestorDeal, saveInvestorDeal, applyInvRentOverride,
  switchOfferStrategy,
  // Portfolio + Providers (Phase 3D)
  openAddPortfolioModal,
  openPortfolioIncomeModal, openPortfolioExpenseModal, deletePortfolioProperty,
  deleteSavedDeal, convertDealToProperty, lookupPropertyAutofill,
  // Phase 1C/1D
  openPortfolioDetail, switchPortfolioTab, deletePortfolioTransaction,
  savePortfolioInsurance, clearPortfolioInsurance, dismissInsuranceNudge,
  sortPortfolioBy, openPortfolioQuickTx,
  openDealAnalyzer, onDealAnalyzerInput,
  // Phase 2B
  openExchangeModal, addIdentifiedProperty, removeIdentifiedProperty,
  computeExchangeComparison, toggleExchangeReturnExtension,
  // Phase 2A/2C/2E
  openCapexModal, onCapexComponentChange, deleteCapex,
  openRepsModal,
  // Phase 3A/3B/3C
  switchCompareMetric, onStressSlider,
  // Phase 4
  openRehabModal, deleteRehabItem,
  // Phase 5
  runCapitalGains,
  // Chunk P1
  onFireInput,
  // Chunk P2
  openPartnerModal, deleteSplit,
  // Mockup polish
  openPortfolioAiStub,
  // Schedule E printable report
  openScheduleEPrint,
  applyProvidersFilter, requestProviderIntroFromList,
  // Feed
  toggleChip, savePreferences,
  switchFeedMode, loadFeedForMode, loadFeed, refreshFeed,
  runSearch, clearSearch, clearFiltersOnly, openFilterDrawer,
  setFilterChip, toggleFilterType, resetFilters, applyFilters,
  onInvFilterSlider,
  showWeakerDeals,
  toggleMarketChip,
  handleSwipe, showCurrentDetail,
  // Map view (Feature 4a)
  switchFeedView, openMapDetail, closeMapPreview, mapSwipe,
  toggleDrawMode, clearDrawBoundary,
  // Tier 1 — unsave + likes drawer controls + locate me
  unlikeListing, likesDrawerSearch, likesDrawerSort, locateMe,
  // Redundancy #2 Option 2 — pipeline actions on Watchlist cards
  openPipelineActions, setDealPipelineStatus,
  // Listings
  openDetail, shareListing, galleryGo, galleryNav,
  clearRecentlyViewed,
  toggleCompareMode, toggleCompareSelection, openCompareScreen,
  // Saved Searches (Feature 9)
  saveCurrentSearch, applySavedSearchRaw, toggleSavedSearchNotify, confirmDeleteSavedSearch,
  // Photo gallery (Feature 2)
  openPhotoGallery, openPhotoGalleryForListing, closePhotoGallery, pgNext, pgPrev,
  // Profile & Onboarding
  editProfile, saveProfile, handleLogout, submitPrivacyRequest,
  resetDemoProfile, runDemoReset,
  navigateToHome, loadNotificationCount,
  onboardingNext, onboardingBack, completeOnboarding,
  dismissOnboardingProgress, handleOnboardingStepClick,
  showSwipeTutorial,
  onboardingUseGPS, onboardingSelectArea, onboardingSaveLocation,
  onboardingUpdateBudget, onboardingSetBudget,
  onboardingSaveBudget,
  onboardingSelectBeds, onboardingSelectBaths, onboardingToggleType,
  onboardingSavePrefs,
  dismissSwipeTutorial,
  // Notifications
  loadNotifications, markNotifRead, markAllRead, dismissNotification,
  clearNotificationsPrompt, clearNotifications, setNotifFilter,
  toggleNotifPref, updateQuietHours, toggleBatchDigest,
  // Trust Layer
  toggleHomecheckFactors,
  // Utilities exposed for modules
  formatPrice, timeAgo, getInitials, formatListingAddress,
  getMediaUrl, normalizePhotoUrl, imgFallback, getAllMedia,
  getUserName,
  loadProfileStats,
  loadPreferences,
  renderOnboardingProgressCard, checkSwipeMilestones,
  restoreOnboardingFlags, saveOnboardingFlag,
  isHomecheckDemo, hcScoreColor, hcCondBadge,
  renderHomecheckFull, renderHomecheckComingSoon,
  loadHomeCheck, loadHomecheckSwipeBadge, loadHomecheckListBadges,
  saveFeedState, restoreFeedState, clearFeedState, getUserLocation,
  renderCurrentCard, updateTabIndicator, updateLocationBar,
  buildFeedParams, initFeedWithLocation,
  renderNotificationPrefs, loadNotificationPrefs,
  ListControls,
  NOTIF_PRIORITY_MAP, getNotifPriority, getNotifIcon, getNotifIconType,
  batchNotifications,
  retryPendingPreferences,
});

// ═══════════════════════════════════════════════════════
// App Initialization
// ═══════════════════════════════════════════════════════
document.addEventListener('DOMContentLoaded', () => {
  const splash = document.getElementById('splash-screen');

  // Feature 4c: restore feed/likes view-mode preferences from
  // localStorage before any screen renders so the user comes back
  // to whichever view they left in (swipe/map/list on discover,
  // list/map on likes).
  restoreViewPrefs();

  // Restore session while splash shows
  let isAuthenticated = false;
  const savedToken = localStorage.getItem('swipehome_token');
  const savedUser = localStorage.getItem('swipehome_user');
  if (savedToken && savedUser) {
    try {
      state.token = savedToken;
      state.user = JSON.parse(savedUser);
      state.role = 'buyer';
      isAuthenticated = true;
      restoreOnboardingFlags();
      // Refresh the profile from the backend in the background while the
      // splash shows, so the UI never renders stale criteria (e.g. after a
      // demo reset from another session or device). Falls back to the
      // cached profile on network/server errors; a 401 clears the dead
      // token so the user gets the login screen instead of a broken shell.
      void (async () => {
        try {
          const fresh = await api<Record<string, unknown>>('GET', '/users/me');
          if (fresh && typeof fresh === 'object' && fresh.id) {
            state.user = fresh as unknown as typeof state.user;
            localStorage.setItem('swipehome_user', JSON.stringify(state.user));
            restoreOnboardingFlags();
          }
        } catch (err) {
          if (err instanceof Error && /session expired/i.test(err.message)) {
            state.token = null;
            state.user = null;
            isAuthenticated = false;
            localStorage.removeItem('swipehome_token');
            localStorage.removeItem('swipehome_user');
          }
        }
      })();
    } catch (e) {
      localStorage.removeItem('swipehome_token');
      localStorage.removeItem('swipehome_user');
    }
  }

  // Check for hash routes
  const hash = window.location.hash;
  const unsubMatch = hash.match(/^#unsubscribe=(.+)$/);
  // Tier 2 — shared-listing deep link (#listing=<uuid>)
  const listingMatch = hash.match(/^#listing=([0-9a-f-]{36})$/i);

  // Show splash for 2.5s, then fade out and navigate
  setTimeout(() => {
    if (splash) {
      splash.classList.add('splash-fade-out');
      setTimeout(() => splash.remove(), 500);
    }

    // Handle unsubscribe deep link
    if (unsubMatch) {
      const agentId = unsubMatch[1];
      api('GET', `/claim/unsubscribe?agent=${encodeURIComponent(agentId)}`).catch(() => {});
      const appEl = document.getElementById('app');
      if (appEl) appEl.innerHTML = `
        <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;background:#0f0f1a;color:#fff;font-family:-apple-system,sans-serif;text-align:center;padding:20px;">
          <div>
            <h1 style="font-size:24px;margin-bottom:12px;">You've been unsubscribed</h1>
            <p style="color:#9ca3af;">You will no longer receive emails from SwipeHome.</p>
          </div>
        </div>`;
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }

    // Shared-listing deep link. If the user is authenticated, open
    // the detail view; otherwise send them to login first and we'll
    // route to the detail post-login (via the same localStorage
    // `sh_pending_listing_id` mechanism we use for other deep links).
    if (listingMatch) {
      const listingId = listingMatch[1]!;
      window.history.replaceState(null, '', window.location.pathname);
      if (isAuthenticated) {
        navigateToHome();
        setTimeout(() => {
          const w = window as unknown as { openDetail?: (id: string) => void };
          w.openDetail?.(listingId);
        }, 300);
        return;
      }
      try { sessionStorage.setItem('sh_pending_listing_id', listingId); } catch { /* ignore */ }
    }

    if (isAuthenticated) {
      navigateToHome();
      retryPendingPreferences();
    } else if (sessionStorage.getItem('sh_gate_unlocked') === '1') {
      state.currentScreen = 'login';
      render();
    } else {
      state.currentScreen = 'gateKey';
      render();
    }
  }, 2500);
});
